import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { siteConfig } from '@/config/site';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import LogoWatermark from '@/components/LogoWatermark';
import useScrollReveal from '@/hooks/useScrollReveal';
import './legal.css';

/**
 * Casa de Candy — premium legal page shell.
 *
 * - Same Navbar + Footer as the main site for full visual consistency.
 * - Global diagonal logo watermark behind the content.
 * - Apple-style glass panels (`legal-section`) with reveal-on-scroll.
 */
export function LegalLayout({ title, kicker = 'Casa de Candy', updated, notice, children, testId }) {
  // Pull the html/body backgrounds back to the brand palette while this page
  // is mounted, so any earlier inline styles never bleed through.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlBg = html.style.background;
    const prevBodyBg = body.style.background;
    const prevBodyColor = body.style.color;

    html.style.background = '#07060B';
    body.style.background = '#07060B';
    body.style.color = '#F4F2FF';

    return () => {
      html.style.background = prevHtmlBg;
      body.style.background = prevBodyBg;
      body.style.color = prevBodyColor;
    };
  }, []);

  // Apple-style scroll reveal for legal sections
  useScrollReveal([title]);

  return (
    <div className="casa-legal min-h-screen flex flex-col" data-testid={testId}>
      <Helmet>
        <title>{title} — Casa de Candy</title>
        <meta name="description" content={`${title} for Casa de Candy.`} />
      </Helmet>

      {/* Global diagonal logo watermark — premium overlay behind everything */}
      <LogoWatermark intensity="legal" />

      {/* Same nav as the main site (logo, contact icons, language toggle) */}
      <Navbar />

      <main className="flex-1 pt-28 sm:pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="legal-back" data-testid="legal-back-home">
            ← Home
          </Link>

          {/* Hero card — branded title block, also a glass panel */}
          <header className="legal-hero glass-panel" data-reveal>
            <div className="legal-brand">
              <img src={siteConfig.brand.logoUrl} alt="Casa de Candy" />
            </div>
            <div className="legal-kicker">{kicker}</div>
            <h1 className="legal-title">{title}</h1>
            {updated && <div className="legal-updated">Last Updated: {updated}</div>}
          </header>

          {notice && (
            <div className="legal-notice glass-panel" data-reveal>
              {notice}
            </div>
          )}

          <article className="legal-article">{children}</article>

          <nav className="legal-page-links glass-panel" data-reveal aria-label="Legal navigation">
            <Link to="/privacy" data-testid="legal-link-privacy">Privacy Policy</Link>
            <span aria-hidden>·</span>
            <Link to="/terms" data-testid="legal-link-terms">Terms of Service</Link>
            <span aria-hidden>·</span>
            <Link to="/nda" data-testid="legal-link-nda">NDA</Link>
          </nav>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default LegalLayout;
